import * as React from "react"

type Theme = "dark" | "light" | "system"

type ThemeProviderProps = {
	children: React.ReactNode
	defaultTheme?: Theme
	storageKey?: string
}

type ThemeProviderState = {
	theme: Theme
	setTheme: (theme: Theme) => void
}

const ThemeProviderContext =
	React.createContext<ThemeProviderState | undefined>(undefined)

export function ThemeProvider({
	                              children,
	                              defaultTheme = "system",
	                              storageKey = "ozon-ai-theme",
                              }: ThemeProviderProps) {
	const [theme, setThemeState] = React.useState<Theme>(() => {
		return (localStorage.getItem(storageKey) as Theme) || defaultTheme
	})

	React.useEffect(() => {
		const root = document.documentElement

		const applyTheme = () => {
			root.classList.remove("light", "dark")

			if (theme === "system") {
				const systemTheme = window.matchMedia(
					"(prefers-color-scheme: dark)",
				).matches
					? "dark"
					: "light"

				root.classList.add(systemTheme)
			} else {
				root.classList.add(theme)
			}
		}

		applyTheme()

		if (theme !== "system") {
			return
		}

		const media = window.matchMedia("(prefers-color-scheme: dark)")
		media.addEventListener("change", applyTheme)

		return () => media.removeEventListener("change", applyTheme)
	}, [theme])

	const setTheme = React.useCallback(
		(theme: Theme) => {
			localStorage.setItem(storageKey, theme)
			setThemeState(theme)
		},
		[storageKey],
	)

	return (
		<ThemeProviderContext.Provider value={{ theme, setTheme }}>
			{children}
		</ThemeProviderContext.Provider>
	)
}

export function useTheme() {
	const context = React.useContext(ThemeProviderContext)

	if (!context) {
		throw new Error("useTheme must be used within ThemeProvider")
	}

	return context
}